import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FacetFilterRail } from "../../../components/UIToolkit/Filters/FacetFilterRail";
import { FilteredEmptyState } from "../../../components/UIToolkit/Filters/FilteredEmptyState";
import { FilterRailFrame } from "../../../components/UIToolkit/Filters/FilterRailFrame";
import type { FacetSpec } from "../../../components/UIToolkit/Filters/facetSections";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../components/UIToolkit/molecules/SettingsSection";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useFilterRailHandoff, useGridFacetFilters } from "../../../hooks/useFacetFilters";
import type { PbjBreaksRemovedRow } from "../../../utils/models";
import { usePbjBreaksRemoved } from "../hooks/usePbjAudit";
import type { WithBuilding } from "../network/buildingQueries";
import { useBuildingGridParts, useWithBuildingFacet } from "../network/useBuildingGridParts";
import { dataTableSx, sectionCardSx } from "../PredictionTab/colors";
import { useFacetLabels } from "../pbjFacetLabels";
import { dayTypeFacet, monthFacet, textFacet } from "../pbjFacets";
import { boldNameColumn, hoursColumn, monospaceIdColumn, sectionFrameHeight } from "../pbjGridCells";
import { formatAuditDate } from "../pbjGridFormat";
import { pbjSortOptions } from "../pbjGridSorting";

type Props = {
	startDate: string;
	endDate: string;
};

/** Unique grid id — fold in the array index so equal (emp, day) rows never collide. */
type BreaksRemovedGridRow = WithBuilding<PbjBreaksRemovedRow> & { __rowId: string };

/**
 * Breaks Removed audit — the (employee, day) rows whose worked hours were trimmed by an
 * unpaid-break rule before filing. Renders as a section card on the Audit canvas, sharing the
 * grid + header treatment of its sibling audit sections.
 */
const COLUMN_FACET: Readonly<Record<string, string>> = { jobDescription: "job", date: "month" };

export const BreaksRemovedTable: FC<Props> = ({ startDate, endDate }) => {
	const { t } = useTranslation();
	const { data, isLoading, isError } = usePbjBreaksRemoved(startDate, endDate);
	const building = useBuildingGridParts<BreaksRemovedGridRow>();

	const rows = useMemo<BreaksRemovedGridRow[]>(
		() => (data ?? []).map((row, index) => ({ ...row, __rowId: `${index}|${row.buildingId}|${row.empId}|${row.date}` })),
		[data],
	);

	const columns: GridColDef<BreaksRemovedGridRow>[] = useMemo(
		() => [
			...building.columns,
			monospaceIdColumn<BreaksRemovedGridRow>("empId", t("pbj_model.audit.breaks_removed.cols.id")),
			boldNameColumn<BreaksRemovedGridRow>("name", t("pbj_model.audit.breaks_removed.cols.name")),
			{
				field: "jobDescription",
				headerName: t("pbj_model.audit.breaks_removed.cols.job"),
				minWidth: 160,
				flex: 1,
				valueFormatter: (value: string | null) => value || "—",
			},
			{
				field: "date",
				headerName: t("pbj_model.audit.breaks_removed.cols.date"),
				minWidth: 140,
				flex: 1,
				valueFormatter: (value) => formatAuditDate(value),
			},
			hoursColumn<BreaksRemovedGridRow>("breakHoursRemoved", t("pbj_model.audit.breaks_removed.cols.break_hours"), { minWidth: 140 }),
		],
		[t, building.columns],
	);

	const labels = useFacetLabels();
	const facets = useMemo<FacetSpec<BreaksRemovedGridRow>[]>(
		() => [
			...building.facets,
			{
				dimension: textFacet<BreaksRemovedGridRow>("job", (r) => r.jobDescription),
				title: t("pbj_model.audit.breaks_removed.cols.job"),
				getLabel: labels.raw,
				searchable: true,
			},
			{ dimension: monthFacet<BreaksRemovedGridRow>("month", (r) => r.date), title: t("pbj_model.filter.month"), getLabel: labels.month, order: "key" },
			{ dimension: dayTypeFacet<BreaksRemovedGridRow>("day_type", (r) => r.date), title: t("pbj_model.filter.day_type"), getLabel: labels.dayType },
		],
		[t, labels, building.facets],
	);
	const filters = useGridFacetFilters(rows, facets, building.sharedGroups);
	const railHandoff = useFilterRailHandoff(filters.highlight, useWithBuildingFacet(COLUMN_FACET), t("pbj_model.filter.in_rail"));

	const sortingOptions = useMemo(
		() =>
			pbjSortOptions<BreaksRemovedGridRow>([
				...building.sortFields,
				{ field: "empId", label: t("pbj_model.audit.breaks_removed.cols.id") },
				{ field: "name", label: t("pbj_model.audit.breaks_removed.cols.name") },
				{ field: "jobDescription", label: t("pbj_model.audit.breaks_removed.cols.job") },
				{ field: "date", label: t("pbj_model.audit.breaks_removed.cols.date") },
				{ field: "breakHoursRemoved", label: t("pbj_model.audit.breaks_removed.cols.break_hours") },
			]),
		[t, building.sortFields],
	);

	return (
		<SettingsSection
			layout="stack"
			title={t("pbj_model.audit.breaks_removed.section.title")}
			description={t("pbj_model.audit.breaks_removed.section.subtitle")}
			dataTestId="pbj-breaks-removed-section"
			sx={(theme) => sectionCardSx(theme)}
		>
			<FilterRailFrame
				flat
				height={sectionFrameHeight(rows.length)}
				dataTestId="pbj-breaks-removed-filters-frame"
				rail={
					<FacetFilterRail
						rows={rows}
						facets={facets}
						filters={filters}
						collapsed={railHandoff.railCollapsed}
						onCollapsedChange={railHandoff.setRailCollapsed}
						dataTestId="pbj-breaks-removed-filters"
					/>
				}
			>
				<SystemDataGrid<BreaksRemovedGridRow>
					rows={filters.result.rows}
					columns={columns}
					getRowId={(r) => r.__rowId}
					searchableColumns={["empId", "name", "jobDescription"]}
					canFilterByColumn={railHandoff.canFilterByColumn}
					onFilterByColumn={railHandoff.onFilterByColumn}
					filterByHint={railHandoff.filterByHint}
					sortingOptions={sortingOptions}
					showSortSummary
					exportOptions={{ fileName: t("pbj_model.audit.breaks_removed.section.title") }}
					searchPlaceholder={t("pbj_model.audit.breaks_removed.search")}
					loading={isLoading}
					error={isError}
					errorMessage={t("pbj_model.audit.breaks_removed.failed")}
					emptyState={
						<FilteredEmptyState
							filtered={filters.activeCount > 0}
							onClearFilters={filters.clear}
							unfilteredState={<EmptyState title={t("pbj_model.audit.breaks_removed.empty")} dataTestId="pbj-model-audit-breaks-removed-empty" />}
							dataTestId="pbj-model-audit-breaks-removed-filtered-empty"
						/>
					}
					defaultPageSize={25}
					sx={(theme) => dataTableSx(theme)}
					dataTestId="pbj-model-audit-breaks-removed"
				/>
			</FilterRailFrame>
		</SettingsSection>
	);
};
